import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Modal,
  TextField,
  InputAdornment,
  Divider,
} from "@mui/material";

const API_BASE = "https://appapi.unpaidfinancial.com/api";

export default function ImagesExplorer() {
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const token = useMemo(() => localStorage.getItem("accessToken"), []);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await fetch(`${API_BASE}/demandes`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        setClients(Array.isArray(data) ? data : []);
        setFiltered(Array.isArray(data) ? data : []);
      } catch (e) {
        setClients([]);
        setFiltered([]);
      }
    };
    fetchClients();
  }, [token]);

  useEffect(() => {
    const s = search.trim().toLowerCase();
    if (!s) {
      setFiltered(clients);
    } else {
      setFiltered(
        clients.filter(
          (c) =>
            c.nom?.toLowerCase().includes(s) ||
            c.prenom?.toLowerCase().includes(s) ||
            c.email?.toLowerCase().includes(s)
        )
      );
    }
  }, [search, clients]);

  const handleOpen = (client) => {
    setSelected(client);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelected(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        📁 Fichiers des clients
      </Typography>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, prénom ou email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <i className="fas fa-search" style={{ color: "#aaa" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {filtered.length === 0 && (
          <Grid item xs={12}>
            <Typography>Aucun client trouvé.</Typography>
          </Grid>
        )}

        {filtered.map((client) => (
          <Grid key={client.id} item xs={12} sm={6} md={4} lg={3}>
            <Card>
              <CardActionArea onClick={() => handleOpen(client)}>
                <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
                  <Avatar sx={{ bgcolor: "#647dee", mr: 2 }}>
                    {client.prenom?.[0]?.toUpperCase() || "?"}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {client.prenom} {client.nom}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {client.email}
                    </Typography>
                  </Box>
                </Box>

                <CardContent>
                  <Typography variant="caption" color="primary">
                    Voir ses documents &rarr;
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Modal d’affichage des documents */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            width: "min(92vw, 880px)",
            maxHeight: "86vh",
            overflowY: "auto",
          }}
        >
          {selected && (
            <>
              <Typography variant="h5" gutterBottom>
                📁 {selected.prenom} {selected.nom}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Email : {selected.email}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                {/* Colonne Identité */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <i
                      className="fas fa-id-card"
                      style={{ marginRight: 8, color: "#647dee" }}
                    />
                    Pièce d’identité
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DocumentImageAuth
                        url={`${API_BASE}/documents/${selected.id}/idcard`}
                        label="Recto (idcard)"
                        token={token}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentImageAuth
                        url={`${API_BASE}/documents/${selected.id}/idcard2`}
                        label="Verso (idcard2)"
                        token={token}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Colonne Domicile */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <i
                      className="fas fa-file-alt"
                      style={{ marginRight: 8, color: "#647dee" }}
                    />
                    Justificatif de domicile
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DocumentImageAuth
                        url={`${API_BASE}/documents/${selected.id}/homeproof`}
                        label="Photo 1 (homeproof)"
                        token={token}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentImageAuth
                        url={`${API_BASE}/documents/${selected.id}/homeproof2`}
                        label="Photo 2 (homeproof2)"
                        token={token}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
}

/**
 * ✅ <img src="URL"> ne peut pas envoyer Authorization header.
 * Donc on fetch en blob avec Bearer, puis on affiche via objectURL.
 */
function DocumentImageAuth({ url, label, token }) {
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const [blobUrl, setBlobUrl] = useState("");

  useEffect(() => {
    let alive = true;
    let localObjectUrl = "";

    const load = async () => {
      setLoading(true);
      setImgError(false);
      setBlobUrl("");

      try {
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const blob = await res.blob();
        localObjectUrl = URL.createObjectURL(blob);

        if (!alive) return;
        setBlobUrl(localObjectUrl);
      } catch (e) {
        if (!alive) return;
        setImgError(true);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
      if (localObjectUrl) URL.revokeObjectURL(localObjectUrl);
    };
  }, [url, token]);

  return (
    <Box>
      <Typography fontWeight={600} fontSize={14} sx={{ mb: 0.5 }}>
        {label}
      </Typography>

      <Box
        sx={{
          border: "1px solid #eee",
          borderRadius: 2,
          overflow: "hidden",
          height: 190,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fafafa",
        }}
      >
        {loading ? (
          <Typography fontSize={13} color="text.secondary">
            Chargement…
          </Typography>
        ) : imgError ? (
          <Typography fontSize={13} color="error">
            Image non trouvée ou non envoyée.
          </Typography>
        ) : (
          <img
            src={blobUrl}
            alt={label}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onError={() => setImgError(true)}
          />
        )}
      </Box>
    </Box>
  );
}
